import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Loader2 } from "lucide-react";

interface ContactStepProps {
  email: string;
  phone: string;
  message: string;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  firstName: string;
}

const ContactStep = ({ 
  email, 
  phone,
  message,
  onEmailChange, 
  onPhoneChange,
  onMessageChange,
  onSubmit, 
  onBack,
  isSubmitting,
  firstName
}: ContactStepProps) => {
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col items-center text-center"
    >
      <div className="mb-6">
        <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
        <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-2">
          Almost there{firstName ? `, ${firstName}` : ""}!
        </h2>
        <p className="text-muted-foreground">
          Where should we send your personalized brochure?
        </p>
      </div>

      <div className="w-full max-w-md space-y-4 mb-8">
        <div className="space-y-2 text-left">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="your@email.com"
            className="text-center md:text-left"
          />
        </div>
        
        <div className="space-y-2 text-left">
          <Label htmlFor="phone">Phone Number (optional)</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="(555) 123-4567"
            className="text-center md:text-left"
          />
        </div>

        <div className="space-y-2 text-left">
          <Label htmlFor="message">Anything else we should know? (optional)</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder="Tell us about your vision, questions, or special requests..."
            rows={3}
          />
        </div>
        
        <p className="text-xs text-muted-foreground">
          We respect your privacy and will never share your information.
        </p>
      </div>

      <div className="flex gap-3 w-full max-w-md">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!isValidEmail || isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            "Get My Brochure"
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default ContactStep;
